import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// State tied to one browser history entry: Back / Forward onto the same entry restores it exactly
// (filters, search, scroll), while a fresh visit starts clean.
const read = (k) => { try { const v = sessionStorage.getItem(k); return v === null ? undefined : JSON.parse(v); } catch { return undefined; } };

export function useEntryState(name, initial) {
  const { key } = useLocation();
  const storeKey = `entry:${key}:${name}`;
  const [value, setValue] = useState(() => { const v = read(storeKey); return v === undefined ? initial : v; });
  useEffect(() => { sessionStorage.setItem(storeKey, JSON.stringify(value)); }, [storeKey, value]);
  return [value, setValue];
}

// Remembers a scroll box's position for this history entry and restores it on return, retrying
// briefly while the content is still loading (stops as soon as the person scrolls).
export function useEntryScroll(ref) {
  const { key } = useLocation();
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const storeKey = `entry:${key}:scroll`;
    const target = Number(sessionStorage.getItem(storeKey) || 0);
    let tries = 0;
    let timer = null;
    const restore = () => {
      if (!target || el.scrollTop >= target - 2 || tries++ > 30) return;
      el.scrollTop = target;
      timer = setTimeout(restore, 100);
    };
    restore();
    const save = () => sessionStorage.setItem(storeKey, String(el.scrollTop));
    const stop = () => { tries = 99; };
    el.addEventListener("scroll", save, { passive: true });
    el.addEventListener("wheel", stop, { passive: true });
    el.addEventListener("touchstart", stop, { passive: true });
    return () => {
      clearTimeout(timer);
      el.removeEventListener("scroll", save);
      el.removeEventListener("wheel", stop);
      el.removeEventListener("touchstart", stop);
    };
  }, [ref, key]);
}
